import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const PARTNER_TYPES = ["service_provider", "reseller", "agent"];

export default function AdminPartners() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [psms, setPsms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", partner_type: "reseller", contact_email: "", contact_phone: "", website: "", address: "", psm_id: "" });

  const load = async () => {
    const [p, u] = await Promise.all([api.get("/partners"), api.get("/users").catch(() => ({ data: [] }))]);
    setPartners(p.data || []);
    setPsms((u.data || []).filter((x) => x.role === "partner_success_manager"));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/partners", { ...form, psm_id: form.psm_id || null });
      toast.success("Partner created");
      setOpen(false);
      setForm({ name: "", partner_type: "reseller", contact_email: "", contact_phone: "", website: "", address: "", psm_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setType = async (id, partner_type) => { await api.patch(`/partners/${id}`, { partner_type }); load(); };
  const setPsm = async (id, psm_id) => { await api.patch(`/partners/${id}`, { psm_id }); load(); };
  const remove = async (id) => { if (confirm("Delete partner?")) { await api.delete(`/partners/${id}`); load(); } };

  const canEdit = ["super_user", "partner_success_manager"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="admin-partners-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Admin</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-1">Partners</h1>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="new-partner-btn"><Plus size={16} className="mr-1" /> New partner</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-md">
              <DialogHeader><DialogTitle>Create partner company</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Company name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm mt-1" data-testid="partner-name" /></div>
                <div><Label>Partner type</Label>
                  <Select value={form.partner_type} onValueChange={(v) => setForm({...form, partner_type: v})}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="partner-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{PARTNER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="rounded-sm mt-1" /></div>
                <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} className="rounded-sm mt-1" /></div>
                <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} className="rounded-sm mt-1" /></div>
                <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="rounded-sm mt-1" /></div>
                {user?.role === "super_user" && psms.length > 0 && (
                  <div><Label>Assigned PSM</Label>
                    <Select value={form.psm_id} onValueChange={(v) => setForm({...form, psm_id: v})}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>{psms.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={submit} className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="partner-save">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="rounded-sm border-border overflow-hidden">
        <table className="w-full text-sm dense">
          <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th>Company</th><th>Type</th><th>Contact</th><th>PSM</th><th>Website</th><th></th></tr>
          </thead>
          <tbody>
            {partners.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No partners yet.</td></tr>}
            {partners.map((p) => (
              <tr key={p.partner_id} className="border-t border-border" data-testid={`partner-row-${p.partner_id}`}>
                <td className="font-medium">{p.name}</td>
                <td>
                  {canEdit ? (
                    <Select value={p.partner_type} onValueChange={(v) => setType(p.partner_id, v)}>
                      <SelectTrigger className="h-8 w-40 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{PARTNER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-xs">{p.partner_type}</span>}
                </td>
                <td className="text-muted-foreground">{p.contact_email}<div className="text-xs">{p.contact_phone}</div></td>
                <td>
                  {user?.role === "super_user" && psms.length > 0 ? (
                    <Select value={p.psm_id || ""} onValueChange={(v) => setPsm(p.partner_id, v)}>
                      <SelectTrigger className="h-8 w-44 rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{psms.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-xs text-muted-foreground">{psms.find(u => u.user_id === p.psm_id)?.name || "—"}</span>}
                </td>
                <td className="text-xs text-muted-foreground">{p.website || "—"}</td>
                <td className="text-right">{user?.role === "super_user" &&
                  <button className="p-2 text-muted-foreground hover:text-destructive" onClick={() => remove(p.partner_id)} data-testid={`partner-del-${p.partner_id}`}><Trash size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
